import React from "react";
import { type ClassNames, type DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "../cn";
import { ChevronDown, ChevronLeft, ChevronRight } from "../icons";
import { buttonVariants } from "./button";

const defaultClassNames = getDefaultClassNames();

const redesignClassNames: Partial<ClassNames> = {
  root: cn(defaultClassNames.root, "tw-w-fit"),
  months: cn(defaultClassNames.months, "tw-relative tw-flex tw-flex-col"),
  month: cn(defaultClassNames.month, "tw-flex tw-w-full tw-flex-col tw-gap-4"),
  // Full-width flex positioning works with DayPicker's default navigation layout.
  nav: "tw-absolute tw-inset-x-0 tw-top-0 tw-flex tw-w-full tw-items-center tw-justify-between",
  button_previous: cn(buttonVariants({ variant: "outline", size: "icon" }), "tw-size-9 tw-rounded-lg tw-shadow-sm"),
  button_next: cn(buttonVariants({ variant: "outline", size: "icon" }), "tw-size-9 tw-rounded-lg tw-shadow-sm"),
  chevron: "tw-size-4 tw-text-foreground",
  month_caption: "tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-center tw-px-9 tw-text-sm tw-font-medium tw-capitalize tw-text-foreground",
  dropdowns: "tw-relative tw-inline-flex tw-items-center tw-gap-2",
  dropdown_root: "tw-relative tw-inline-flex tw-items-center",
  dropdown: "tw-absolute tw-inset-0 tw-z-[2] tw-m-0 tw-w-full tw-cursor-pointer tw-appearance-none tw-border-0 tw-p-0 tw-opacity-0",
  caption_label: "tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-capitalize tw-text-foreground",
  month_grid: cn(defaultClassNames.month_grid, "tw-w-full tw-border-collapse"),
  weekdays: cn(defaultClassNames.weekdays, "tw-flex"),
  weekday: "tw-flex-1 tw-select-none tw-text-center tw-text-xs tw-font-normal tw-text-muted-foreground",
  week: cn(defaultClassNames.week, "tw-mt-2 tw-flex tw-w-full"),
  day: cn(defaultClassNames.day, "tw-p-0.5 tw-text-center"),
  outside: "tw-text-muted-foreground tw-opacity-50",
  disabled: "tw-pointer-events-none tw-text-muted-foreground tw-opacity-50",
  today: "[&>button]:tw-font-semibold [&>button]:tw-text-primary",
};

const dayButtonClassName = cn(
  buttonVariants({ variant: "ghost", size: "icon" }),
  "tw-aspect-square tw-size-9 tw-font-normal",
  "data-[selected-single=true]:tw-bg-primary data-[selected-single=true]:tw-text-primary-foreground data-[selected-single=true]:hover:tw-bg-primary",
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- `day` must be destructured out of props (not a valid <button> attribute); its value isn't needed here.
function CalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
  return <button type="button" data-selected-single={modifiers["selected"]} className={cn(dayButtonClassName, className)} {...props} />;
}

export function Calendar({ className, classNames, components, ...props }: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      className={cn("tw-w-fit tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-p-4 tw-shadow-md", className)}
      classNames={{ ...redesignClassNames, ...classNames }}
      components={{
        Root: ({ className: rootClassName, rootRef, ...rootProps }) => <div data-slot="calendar" ref={rootRef} className={rootClassName} {...rootProps} />,
        Chevron: ({ className: chevronClassName, orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : orientation === "right" ? ChevronRight : ChevronDown;
          return <Icon className={cn("tw-size-4", chevronClassName)} />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}
